import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Briefcase, Building2, CreditCard, Eye, LoaderCircle, Plus, Star, Store, Trash2, TriangleAlert, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/landing/Reveal";
import { RESOURCE_TYPES, categoryLabel, priceLabel } from "@/lib/partners";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";

const CONTRACTS = ["CDI", "CDD", "Intérim", "Alternance", "Stage", "Autre"];
const EMPTY_JOB = { title: "", contract_type: "CDI", location: "", description: "", apply_email: "", apply_url: "" };

export default function EspacePartenaire() {
  const { user, setUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resForm, setResForm] = useState({ title: "", type: RESOURCE_TYPES[0].value, content: "", url: "" });
  const [adding, setAdding] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobForm, setJobForm] = useState(EMPTY_JOB);
  const [addingJob, setAddingJob] = useState(false);
  const [stats, setStats] = useState(null);

  const isRecruiter = user?.partner?.category === "offre_emploi";

  useEffect(() => {
    if (user?.role === "partner" && isRecruiter) api.get("/partners/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, [user, isRecruiter]);

  useEffect(() => {
    if (user?.role === "partner") api.get("/partners/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  const partner = user?.partner;

  useEffect(() => {
    if (partner && !profile) {
      setProfile({ organization: partner.organization || "", description: partner.description || "",
        website: partner.website || "", city: partner.city || "", phone: user.phone || "" });
    }
  }, [partner, profile, user]);

  useEffect(() => {
    if (user?.role === "partner") api.get("/partners/resources").then((r) => setResources(r.data)).catch(() => {});
  }, [user]);

  const refreshUser = useCallback(() => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}), [setUser]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || checking) return;
    setChecking(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          toast.success("Paiement confirmé ! Votre compte partenaire est actif.");
          setSearchParams({}, { replace: true });
          setChecking(false);
          return;
        }
      } catch { /* réessaie */ }
      if (attempts < 8) setTimeout(poll, 2000);
      else { setChecking(false); toast.info("Paiement en cours de confirmation. Actualisez dans quelques instants."); }
    };
    poll();
  }, [searchParams, refreshUser, setSearchParams, checking]);

  if (user && user.role !== "partner") return <Navigate to="/dashboard" replace />;
  if (!partner || !profile) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const pending = partner.status === "pending_payment";
  const awaitingValidation = partner.status === "pending_validation";
  const rejected = partner.status === "rejected";

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/partners/checkout", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else if (data.activated) { await refreshUser(); toast.success("Compte partenaire activé !"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de lancer le paiement.");
      setPaying(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/partners/me", profile);
      setUser(data);
      toast.success("Profil partenaire mis à jour.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer le profil.");
    } finally { setSaving(false); }
  };

  const addResource = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.post("/partners/resources", { ...resForm, url: resForm.url || null });
      setResources((r) => [data, ...r]);
      setResForm({ title: "", type: RESOURCE_TYPES[0].value, content: "", url: "" });
      toast.success("Ressource publiée. Elle est visible par les utilisateurs.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'ajouter la ressource.");
    } finally { setAdding(false); }
  };

  const deleteResource = async (id) => {
    try {
      await api.delete(`/partners/resources/${id}`);
      setResources((r) => r.filter((x) => x.id !== id));
      toast.success("Ressource supprimée.");
    } catch { toast.error("Impossible de supprimer la ressource."); }
  };

  const addJob = async (e) => {
    e.preventDefault();
    setAddingJob(true);
    try {
      const { data } = await api.post("/partners/jobs", {
        ...jobForm, apply_email: jobForm.apply_email || null, apply_url: jobForm.apply_url || null });
      setJobs((j) => [data, ...j]);
      setJobForm(EMPTY_JOB);
      toast.success("Offre d'emploi publiée.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de publier l'offre.");
    } finally { setAddingJob(false); }
  };

  const deleteJob = async (id) => {
    try {
      await api.delete(`/partners/jobs/${id}`);
      setJobs((j) => j.filter((x) => x.id !== id));
      toast.success("Offre supprimée.");
    } catch { toast.error("Impossible de supprimer l'offre."); }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15";

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-espace-partenaire">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Store className="h-7 w-7 text-bleu" /> Espace partenaire
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {categoryLabel(partner.category)} {partner.is_nonprofit && "— activité bénévole"} · Gérez votre fiche et vos ressources visibles par les utilisateurs.
        </p>
      </div>

      {awaitingValidation ? (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
          className="card-integra flex flex-wrap items-center gap-4 !border-bleu/25 bg-bleu-light/60 p-5" data-testid="partner-validation-banner">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><BadgeCheck className="h-5 w-5 text-bleu" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold text-bleu">Compte en attente de validation par l'équipe INTEGRA FRANCE</p>
            <p className="mt-0.5 text-xs text-slate-600">
              Votre compte bénévole est gratuit. Notre équipe vérifie votre fiche avant de la rendre visible dans l'annuaire des partenaires.
              Vous recevrez un email dès la validation. Vous pouvez déjà compléter votre fiche et vos ressources ci-dessous.
            </p>
          </div>
        </motion.div>
      ) : rejected ? (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
          className="card-integra flex flex-wrap items-center gap-4 !border-rouge/30 bg-red-50/70 p-5" data-testid="partner-rejected-banner">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-100"><TriangleAlert className="h-5 w-5 text-rouge" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold text-rouge">Compte partenaire non validé</p>
            <p className="mt-0.5 text-xs text-slate-600">
              Votre fiche n'est pas visible dans l'annuaire. Consultez l'email envoyé par notre équipe pour connaître le motif, ou contactez-nous.
            </p>
          </div>
        </motion.div>
      ) : pending ? (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
          className="card-integra flex flex-wrap items-center gap-4 !border-amber-300/60 bg-amber-50/70 p-5" data-testid="partner-pending-banner">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100"><TriangleAlert className="h-5 w-5 text-amber-600" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold text-amber-800">Compte en attente d'activation</p>
            <p className="mt-0.5 text-xs text-slate-600">
              Réglez votre abonnement partenaire ({priceLabel((partner.monthly_amount || 0) / 100)}) pour rendre votre fiche visible par les utilisateurs.
            </p>
          </div>
          <button onClick={pay} disabled={paying || checking} className="btn-primary !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="partner-pay-button">
            {paying || checking ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
            {checking ? "Vérification du paiement…" : "Activer mon compte"}
          </button>
        </motion.div>
      ) : (
        <p className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-bold text-emerald-700" data-testid="partner-active-banner">
          <BadgeCheck className="h-4 w-4" /> Compte partenaire actif — votre fiche est visible dans le tableau de bord des utilisateurs ({priceLabel((partner.monthly_amount || 0) / 100)}).
        </p>
      )}

      {stats && (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.04, ease: EASE }}
          className={`grid gap-3 ${isRecruiter ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`} data-testid="partner-stats-row">
          <div className="card-integra px-4 py-5 text-center" data-testid="partner-stat-views">
            <Eye className="mx-auto h-4 w-4 text-bleu" />
            <p className="mt-1.5 font-display text-2xl font-extrabold text-slate-900">{stats.views.toLocaleString("fr-FR")}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Vues de ma fiche</p>
          </div>
          <div className="card-integra px-4 py-5 text-center" data-testid="partner-stat-rating">
            <Star className="mx-auto h-4 w-4 fill-ambre text-ambre" />
            <p className="mt-1.5 font-display text-2xl font-extrabold text-slate-900">{stats.rating_avg ?? "—"}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Note moyenne</p>
          </div>
          <div className="card-integra px-4 py-5 text-center" data-testid="partner-stat-reviews">
            <Users className="mx-auto h-4 w-4 text-bleu" />
            <p className="mt-1.5 font-display text-2xl font-extrabold text-slate-900">{stats.rating_count}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avis reçus</p>
          </div>
          {isRecruiter && (
            <div className="card-integra px-4 py-5 text-center" data-testid="partner-stat-applications">
              <Briefcase className="mx-auto h-4 w-4 text-bleu" />
              <p className="mt-1.5 font-display text-2xl font-extrabold text-slate-900">{stats.applications}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Candidatures suivies</p>
            </div>
          )}
        </motion.div>
      )}

      {stats?.daily_views && (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.05, ease: EASE }}
          className="card-integra p-5" data-testid="partner-views-chart">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900"><Eye className="h-4 w-4 text-bleu" /> Vues de ma fiche — 30 derniers jours</h2>
          <div className="mt-3 h-36">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.daily_views.map((d) => ({ ...d, label: new Date(`${d.date}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) }))}
                margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E3FAE" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#1E3FAE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={6} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`${v} vue${v > 1 ? "s" : ""}`, null]} separator=""
                  contentStyle={{ fontSize: 11, borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Area type="monotone" dataKey="count" stroke="#1E3FAE" strokeWidth={2} fill="url(#viewsGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      <motion.form onSubmit={saveProfile} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.06, ease: EASE }}
        className="card-integra space-y-3 p-6" data-testid="partner-profile-form">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><Building2 className="h-4 w-4 text-bleu" /> Ma fiche partenaire</h2>
        <input required minLength={2} maxLength={120} value={profile.organization} onChange={(e) => setProfile((p) => ({ ...p, organization: e.target.value }))} placeholder="Nom de l'organisation" className={inputCls} data-testid="partner-profile-org" />
        <textarea required minLength={10} maxLength={1000} rows={4} value={profile.description} onChange={(e) => setProfile((p) => ({ ...p, description: e.target.value }))} placeholder="Description de votre activité" className={inputCls} data-testid="partner-profile-desc" />
        <div className="grid gap-3 sm:grid-cols-3">
          <input value={profile.website} onChange={(e) => setProfile((p) => ({ ...p, website: e.target.value }))} placeholder="Site web" className={inputCls} data-testid="partner-profile-website" />
          <input value={profile.city} onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))} placeholder="Ville" className={inputCls} data-testid="partner-profile-city" />
          <input value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="Téléphone" className={inputCls} data-testid="partner-profile-phone" />
        </div>
        <button type="submit" disabled={saving} className="btn-primary !px-6 !py-2.5 disabled:opacity-50" data-testid="partner-profile-save">
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Enregistrer ma fiche"}
        </button>
      </motion.form>

      <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
        className="card-integra p-6" data-testid="partner-resources-section">
        <h2 className="text-base font-bold text-slate-900">Mes ressources ({resources.length}/30)</h2>
        <p className="mt-1 text-xs text-slate-500">Contacts, tarifs, actions bénévoles, services… tout ce que les utilisateurs doivent connaître.</p>

        <form onSubmit={addResource} className="mt-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
            <input required minLength={2} maxLength={120} value={resForm.title} onChange={(e) => setResForm((f) => ({ ...f, title: e.target.value }))} placeholder="Titre (ex : Permanence gratuite du mardi)" className={inputCls} data-testid="resource-title-input" />
            <select value={resForm.type} onChange={(e) => setResForm((f) => ({ ...f, type: e.target.value }))} className={inputCls} data-testid="resource-type-select">
              {RESOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <textarea required minLength={2} maxLength={2000} rows={3} value={resForm.content} onChange={(e) => setResForm((f) => ({ ...f, content: e.target.value }))} placeholder="Détails : horaires, tarifs, conditions, adresse…" className={inputCls} data-testid="resource-content-input" />
          <div className="flex flex-wrap items-center gap-3">
            <input value={resForm.url} onChange={(e) => setResForm((f) => ({ ...f, url: e.target.value }))} placeholder="Lien (optionnel)" className={`${inputCls} flex-1 min-w-[200px]`} data-testid="resource-url-input" />
            <button type="submit" disabled={adding} className="btn-primary !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="resource-add-button">
              {adding ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> Publier</>}
            </button>
          </div>
        </form>

        <div className="mt-4 space-y-2">
          {resources.length === 0 && <p className="py-4 text-center text-xs text-slate-400" data-testid="resources-empty">Aucune ressource publiée pour le moment.</p>}
          {resources.map((r) => (
            <div key={r.id} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4" data-testid="partner-resource-item">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-bleu-light px-2 py-0.5 text-[10px] font-bold text-bleu">{RESOURCE_TYPES.find((t) => t.value === r.type)?.label || r.type}</span>
                  <p className="text-sm font-bold text-slate-900">{r.title}</p>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{r.content}</p>
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs font-semibold text-bleu hover:underline">{r.url}</a>}
              </div>
              <button onClick={() => deleteResource(r.id)} aria-label="Supprimer la ressource" className="text-slate-300 transition-colors hover:text-rouge" data-testid="resource-delete-button">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </motion.section>

      {isRecruiter && (
        <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.14, ease: EASE }}
          className="card-integra p-6" data-testid="partner-jobs-section">
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><Briefcase className="h-4 w-4 text-bleu" /> Mes offres d'emploi ({jobs.length}/20)</h2>
          <p className="mt-1 text-xs text-slate-500">Annonces structurées visibles par les utilisateurs : poste, contrat, lieu et moyen de candidature.</p>

          <form onSubmit={addJob} className="mt-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_140px_1fr]">
              <input required minLength={2} maxLength={120} value={jobForm.title} onChange={(e) => setJobForm((f) => ({ ...f, title: e.target.value }))} placeholder="Intitulé du poste" className={inputCls} data-testid="job-title-input" />
              <select value={jobForm.contract_type} onChange={(e) => setJobForm((f) => ({ ...f, contract_type: e.target.value }))} className={inputCls} data-testid="job-contract-select">
                {CONTRACTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input required minLength={2} maxLength={120} value={jobForm.location} onChange={(e) => setJobForm((f) => ({ ...f, location: e.target.value }))} placeholder="Lieu (ville, télétravail…)" className={inputCls} data-testid="job-location-input" />
            </div>
            <textarea required minLength={10} maxLength={2000} rows={3} value={jobForm.description} onChange={(e) => setJobForm((f) => ({ ...f, description: e.target.value }))} placeholder="Missions, profil recherché, salaire, niveau de français requis…" className={inputCls} data-testid="job-description-input" />
            <div className="grid gap-3 sm:grid-cols-2">
              <input type="email" value={jobForm.apply_email} onChange={(e) => setJobForm((f) => ({ ...f, apply_email: e.target.value }))} placeholder="Email de candidature" className={inputCls} data-testid="job-email-input" />
              <input value={jobForm.apply_url} onChange={(e) => setJobForm((f) => ({ ...f, apply_url: e.target.value }))} placeholder="Lien de candidature (optionnel)" className={inputCls} data-testid="job-url-input" />
            </div>
            <button type="submit" disabled={addingJob} className="btn-primary !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="job-add-button">
              {addingJob ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> Publier l'offre</>}
            </button>
          </form>

          <div className="mt-4 space-y-2">
            {jobs.length === 0 && <p className="py-4 text-center text-xs text-slate-400" data-testid="jobs-empty">Aucune offre publiée pour le moment.</p>}
            {jobs.map((j) => (
              <div key={j.id} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4" data-testid="partner-job-item">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-bleu px-2 py-0.5 text-[10px] font-bold text-white">{j.contract_type}</span>
                    <p className="text-sm font-bold text-slate-900">{j.title}</p>
                    <span className="text-[11px] text-slate-400">{j.location}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{j.description}</p>
                  <p className="mt-1 text-[11px] font-semibold text-bleu">{j.apply_email || j.apply_url}</p>
                </div>
                <button onClick={() => deleteJob(j.id)} aria-label="Supprimer l'offre" className="text-slate-300 transition-colors hover:text-rouge" data-testid="job-delete-button">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </motion.section>
      )}
    </div>
  );
}
